import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Typography,
  Container,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import { People, Label, ShoppingCart, Edit, Delete, Add, Menu, AccountCircle, Payment } from "@mui/icons-material";
import logo from "../assets/th.jpg";
import api from "../services/api";
import { useNavigate } from "react-router-dom";
import AddLabelClaimDialog from "./dialogs/AddLabelClaimDialog";
import EditLabelClaimDialog from "./dialogs/EditLabelClaimDialog";
import BlockedLabelClaimsDialog from "./dialogs/BlockedLabelClaimsDialog";
import heroImage1 from "../assets/heroimage1.png";

function AdminDashboard() {
  const [stats, setStats] = useState({ user_count: 0, label_count: 0, order_count: 0 });
  const [labelClaims, setLabelClaims] = useState([]);
  const [orders, setOrders] = useState([]);
  const [callSchedules, setCallSchedules] = useState([]);
  const [users, setUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [labelClaim, setLabelClaim] = useState({});
  const [newFile, setNewFile] = useState(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newLabelClaim, setNewLabelClaim] = useState({});
  const [alert, setAlert] = useState(null);
  const [blockedLabelClaimsDialogOpen, setBlockedLabelClaimsDialogOpen] = useState(false);
  const [blockedLabelClaims, setBlockedLabelClaims] = useState([]);
  const [openSidebar, setOpenSidebar] = useState(false);
  const [activeSection, setActiveSection] = useState("orders");
  const [selectedStatuses, setSelectedStatuses] = useState({});
  const [updatingOrders, setUpdatingOrders] = useState({});
  const navigate = useNavigate();

  const drawerWidth = 250;

  const getAuthHeaders = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  useEffect(() => {
    api
      .get("/admin/dashboard", getAuthHeaders())
      .then((response) => setStats(response.data))
      .catch((error) => {
        console.error("Failed to load admin stats", error);
        setAlert({ type: "error", message: error.response?.data?.detail || "You do not have access to this page." });
        setTimeout(() => navigate("/login"), 2000);
      });

    fetchLabelClaims();
    fetchOrders();
    fetchCallSchedules();
    fetchUsers();
  }, [navigate]);

  const fetchLabelClaims = () => {
    api
      .get("/admin/label_claims", getAuthHeaders())
      .then((response) => setLabelClaims(response.data))
      .catch((error) => {
        console.error("Failed to fetch label claims", error);
        setAlert({ type: "error", message: error.response?.data?.detail || "Error fetching label claims." });
      });
  };

  const fetchBlockedLabelClaims = () => {
    api
      .get("/admin/blocked_label_claims", getAuthHeaders())
      .then((response) => setBlockedLabelClaims(response.data))
      .catch((error) => {
        console.error("Failed to fetch blocked label claims", error);
        setAlert({ type: "error", message: error.response?.data?.detail || "Error fetching blocked label claims." });
      });
  };

  const fetchOrders = () => {
    api
      .get("/admin/orders", getAuthHeaders())
      .then((response) => {
        setOrders(response.data);
        const initialStatuses = response.data.reduce((acc, order) => ({
          ...acc,
          [order.order_id]: order.status,
        }), {});
        setSelectedStatuses(initialStatuses);
      })
      .catch((error) => {
        console.error("Failed to fetch orders", error);
        setAlert({ type: "error", message: error.response?.data?.detail || "Error fetching orders." });
      });
  };

  const fetchCallSchedules = () => {
    api
      .get("/admin/call_schedules", getAuthHeaders())
      .then((response) => setCallSchedules(response.data))
      .catch((error) => {
        console.error("Failed to fetch call schedules", error);
        setAlert({ type: "error", message: error.response?.data?.detail || "Error fetching call schedules." });
      });
  };

  const fetchUsers = () => {
    api
      .get("/admin/users", getAuthHeaders())
      .then((response) => setUsers(response.data))
      .catch((error) => {
        console.error("Failed to fetch users", error);
        setAlert({ type: "error", message: error.response?.data?.detail || "Error fetching users." });
      });
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const handleDeleteLabelClaim = (id) => {
    if (window.confirm("Are you sure you want to delete this label claim?")) {
      api
        .delete(`/admin/label_claims/${id}`, getAuthHeaders())
        .then(() => {
          setAlert({ type: "success", message: "Label claim deleted successfully!" });
          fetchLabelClaims();
        })
        .catch((error) => {
          console.error("Failed to delete label claim", error);
          setAlert({ type: "error", message: error.response?.data?.detail || "Error deleting label claim." });
        });
    }
  };

  const handleEditLabelClaim = (labelClaimData) => {
    setLabelClaim(labelClaimData);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setLabelClaim({});
    setNewFile(null);
  };

  const handleAddDialogOpen = () => {
    setOpenAddDialog(true);
  };

  const handleAddDialogClose = () => {
    setOpenAddDialog(false);
    setNewLabelClaim({});
    setNewFile(null);
  };

  const handleBlockedDialogOpen = () => {
    setBlockedLabelClaimsDialogOpen(true);
    fetchBlockedLabelClaims();
  };

  const handleFileChange = (e) => {
    setNewFile(e.target.files[0]);
  };

  const handleUpdateLabelClaim = () => {
    if (!labelClaim.label_claim || !labelClaim.condition || !labelClaim.health_claims) {
      setAlert({ type: "error", message: "Label Claim, Condition, and Health Claims are required fields." });
      return;
    }

    const updatedLabelClaim = {
      grade: labelClaim.grade || "",
      label_claim: labelClaim.label_claim,
      condition: labelClaim.condition,
      ingredients: labelClaim.ingredients || "",
      description: labelClaim.description || "",
      health_claims: labelClaim.health_claims,
      prototype: labelClaim.prototype || false,
      clinical_trials: labelClaim.clinical_trials || false,
      regulatory: labelClaim.regulatory || false,
      dosage: labelClaim.dosage || "",
      format: labelClaim.format || "",
      amount: labelClaim.amount ? parseFloat(labelClaim.amount) : null,
      booking_fee: labelClaim.booking_fee ? parseFloat(labelClaim.booking_fee) : null,
      dossier_file: labelClaim.dossier_file || null,
    };

    const formData = new FormData();
    formData.append("data", JSON.stringify(updatedLabelClaim));
    if (newFile) {
      formData.append("dossier_file", newFile);
    }

    api
      .put(`/admin/label_claims/${labelClaim.id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      .then(() => {
        setAlert({ type: "success", message: "Label claim updated successfully!" });
        fetchLabelClaims();
        handleDialogClose();
      })
      .catch((error) => {
        const errorMessage = error.response?.data?.detail || "Error updating label claim.";
        setAlert({ type: "error", message: errorMessage });
        console.error("Error updating label claim:", error);
      });
  };

  const handleAddNewLabelClaim = () => {
    if (!newLabelClaim.label_claim || !newLabelClaim.condition || !newLabelClaim.health_claims) {
      setAlert({ type: "error", message: "Label Claim, Condition, and Health Claims are required fields." });
      return;
    }

    if (!newFile) {
      setAlert({ type: "error", message: "Please select a dossier file." });
      return;
    }

    const formData = new FormData();
    formData.append("dossier_file", newFile);
    formData.append("label_claim", JSON.stringify({
      grade: newLabelClaim.grade || "",
      label_claim: newLabelClaim.label_claim,
      condition: newLabelClaim.condition,
      ingredients: newLabelClaim.ingredients || "",
      description: newLabelClaim.description || "",
      health_claims: newLabelClaim.health_claims,
      prototype: newLabelClaim.prototype || false,
      clinical_trials: newLabelClaim.clinical_trials || false,
      regulatory: newLabelClaim.regulatory || false,
      dosage: newLabelClaim.dosage || "",
      format: newLabelClaim.format || "",
      amount: newLabelClaim.amount ? parseFloat(newLabelClaim.amount) : null,
      booking_fee: newLabelClaim.booking_fee ? parseFloat(newLabelClaim.booking_fee) : null,
    }));

    api
      .post("/admin/add_label_claim", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      .then(() => {
        setAlert({ type: "success", message: "Label claim added successfully!" });
        fetchLabelClaims();
        handleAddDialogClose();
      })
      .catch((error) => {
        console.error("Error adding label claim:", error);
        setAlert({ type: "error", message: error.response?.data?.detail || "Error adding label claim." });
      });
  };

  const handleStatusChange = (orderId, newStatus) => {
    setSelectedStatuses((prev) => ({
      ...prev,
      [orderId]: newStatus,
    }));
  };

  const handleUpdateOrder = async (orderId, order) => {
    if (!order.label_claim_id) {
      setAlert({ type: "error", message: "No label claim associated with this order." });
      return;
    }
    if (order.remaining_amount > 0 && !window.confirm("Remaining amount is non-zero. Proceed to commit full payment?")) {
      return;
    }
    const newStatus = selectedStatuses[orderId];
    if (newStatus === order.status) {
      setAlert({ type: "info", message: "No status change to commit." });
      return;
    }
    setUpdatingOrders((prev) => ({ ...prev, [orderId]: true }));
    try {
      const updates = {
        status: newStatus,
        payment_status: newStatus === "fully_paid" ? "completed" : order.payment_status,
        amount: newStatus === "fully_paid" ? order.amount + (order.remaining_amount || 0) : order.amount,
        dossier_filename: null,
      };
      await api.patch(`/admin/orders/${orderId}`, { order_update: updates }, getAuthHeaders());
      setAlert({ type: "success", message: "Order updated successfully!" });
      fetchOrders();
    } catch (error) {
      console.error("Error updating order:", error);
      setAlert({ type: "error", message: error.response?.data?.detail || "Error updating order." });
    } finally {
      setUpdatingOrders((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const handleCommitFullPayment = async (orderId, order) => {
    if (!order.label_claim_id) {
      setAlert({ type: "error", message: "No label claim associated with this order." });
      return;
    }
    if (order.remaining_amount > 0 && !window.confirm(`Remaining amount (${order.remaining_amount.toFixed(2)}) is non-zero. Commit full payment for order ${orderId}?`)) {
      return;
    }
    setUpdatingOrders((prev) => ({ ...prev, [orderId]: true }));
    try {
      const updates = {
        status: "fully_paid",
        payment_status: "completed",
        amount: order.amount + (order.remaining_amount || 0),
        dossier_filename: null,
      };
      await api.patch(`/admin/orders/${orderId}`, { order_update: updates }, getAuthHeaders());
      setAlert({ type: "success", message: "Full payment committed!" });
      fetchOrders();
    } catch (error) {
      console.error("Error committing full payment:", error);
      setAlert({ type: "error", message: error.response?.data?.detail || "Error committing full payment." });
    } finally {
      setUpdatingOrders((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const renderContent = () => {
    if (activeSection === "orders") {
      return (
        <>
          <Typography variant="h5">Orders</Typography>
          {alert && <Alert severity={alert.type}>{alert.message}</Alert>}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>User ID</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Razorpay Order ID</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Payment Status</TableCell>
                  <TableCell>Order Status</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Updated At</TableCell>
                  <TableCell>Label Claim ID</TableCell>
                  <TableCell>Remaining Amount</TableCell>
                  <TableCell>GST Amount</TableCell>
                  <TableCell>Booking Fee</TableCell>
                  <TableCell>Additional Services</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.order_id}>
                    <TableCell>{order.order_id}</TableCell>
                    <TableCell>{order.user_id}</TableCell>
                    <TableCell>{order.username}</TableCell>
                    <TableCell>{order.email}</TableCell>
                    <TableCell>{order.razorpay_order_id}</TableCell>
                    <TableCell>{order.amount.toFixed(2)}</TableCell>
                    <TableCell>{order.payment_status}</TableCell>
                    <TableCell>
                      {["booking_paid", "pending"].includes(order.status) ? (
                        <FormControl fullWidth>
                          <InputLabel>Order Status</InputLabel>
                          <Select
                            value={selectedStatuses[order.order_id] || order.status}
                            onChange={(e) => handleStatusChange(order.order_id, e.target.value)}
                            disabled={updatingOrders[order.order_id]}
                          >
                            <MenuItem value="booking_paid">Booking Paid</MenuItem>
                            <MenuItem value="fully_paid">Fully Paid</MenuItem>
                          </Select>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            sx={{ marginTop: 1 }}
                            onClick={() => handleUpdateOrder(order.order_id, order)}
                            disabled={selectedStatuses[order.order_id] === order.status || updatingOrders[order.order_id]}
                          >
                            {updatingOrders[order.order_id] ? <CircularProgress size={20} /> : "Commit"}
                          </Button>
                        </FormControl>
                      ) : (
                        order.status
                      )}
                    </TableCell>
                    <TableCell>{new Date(order.created_at).toLocaleString()}</TableCell>
                    <TableCell>{new Date(order.updated_at).toLocaleString()}</TableCell>
                    <TableCell>{order.label_claim_id || "None"}</TableCell>
                    <TableCell>{order.remaining_amount ? order.remaining_amount.toFixed(2) : ""}</TableCell>
                    <TableCell>{order.gst_amount ? order.gst_amount.toFixed(2) : ""}</TableCell>
                    <TableCell>{order.booking_fee ? order.booking_fee.toFixed(2) : ""}</TableCell>
                    <TableCell>{order.additional_services}</TableCell>
                    <TableCell>
                      {["booking_paid", "pending"].includes(order.status) && (
                        <IconButton
                          onClick={() => handleCommitFullPayment(order.order_id, order)}
                          disabled={updatingOrders[order.order_id]}
                        >
                          {updatingOrders[order.order_id] ? <CircularProgress size={20} /> : <Payment />}
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      );
    } else if (activeSection === "call_schedules") {
      return (
        <>
          <Typography variant="h5">Call Schedules</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Schedule ID</TableCell>
                  <TableCell>User ID</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Order IDs</TableCell>
                  <TableCell>Call Date</TableCell>
                  <TableCell>Call Time</TableCell>
                  <TableCell>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {callSchedules.map((schedule) => (
                  <TableRow key={schedule.id}>
                    <TableCell>{schedule.id}</TableCell>
                    <TableCell>{schedule.user_id}</TableCell>
                    <TableCell>{schedule.username}</TableCell>
                    <TableCell>{schedule.email}</TableCell>
                    <TableCell>{schedule.order_ids}</TableCell>
                    <TableCell>{new Date(schedule.call_date).toLocaleDateString()}</TableCell>
                    <TableCell>{schedule.call_time}</TableCell>
                    <TableCell>{new Date(schedule.created_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      );
    } else if (activeSection === "users") {
      return (
        <>
          <Typography variant="h5">Users</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User ID</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Appointment</TableCell>
                  <TableCell>Company Name</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>User Type</TableCell>
                  <TableCell>Market Segment</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Contact No</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.id}</TableCell>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.role}</TableCell>
                    <TableCell>{user.appointment}</TableCell>
                    <TableCell>{user.company_name}</TableCell>
                    <TableCell>{user.address}</TableCell>
                    <TableCell>{user.user_type}</TableCell>
                    <TableCell>{user.market_segment}</TableCell>
                    <TableCell>{user.country}</TableCell>
                    <TableCell>{user.contact_no}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      );
    }

    return (
      <>
        <Typography variant="h5">Available Label Claims</Typography>
        {alert && <Alert severity={alert.type}>{alert.message}</Alert>}
        <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleBlockedDialogOpen}
            sx={{ marginRight: "10px" }}
          >
            View Blocked Label Claims
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={handleAddDialogOpen}
          >
            Add Label Claim
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Label Claim</TableCell>
                <TableCell>Condition</TableCell>
                <TableCell>Health Claims</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Booking Fee</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {labelClaims.map((labelClaim) => (
                <TableRow key={labelClaim.id}>
                  <TableCell>{labelClaim.label_claim}</TableCell>
                  <TableCell>{labelClaim.condition}</TableCell>
                  <TableCell>{labelClaim.health_claims}</TableCell>
                  <TableCell>{labelClaim.amount ? labelClaim.amount.toFixed(2) : ""}</TableCell>
                  <TableCell>{labelClaim.booking_fee ? labelClaim.booking_fee.toFixed(2) : ""}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleEditLabelClaim(labelClaim)}>
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteLabelClaim(labelClaim.id)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", width: "100%", overflowX: "hidden" }}>
      <Drawer
        variant="persistent"
        anchor="left"
        open={openSidebar}
        sx={{
          width: openSidebar ? drawerWidth : 0,
          flexShrink: 0,
          transition: "width 0.3s",
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            backgroundImage: `url(${heroImage1})`,
            backgroundSize: "cover",
            color: "white",
            paddingTop: "20px",
            overflowX: "hidden",
          },
        }}
      >
        <List>
          <ListItem sx={{ display: "flex", alignItems: "center" }}>
            <IconButton onClick={() => setOpenSidebar(false)}>
              <Menu sx={{ color: "white" }} />
            </IconButton>
            <Typography variant="h6">Admin Dashboard</Typography>
          </ListItem>
          <Divider sx={{ backgroundColor: "rgba(255, 255, 255, 0.2)" }} />
          <ListItem
            button
            onClick={() => {
              setActiveSection("label_claims");
              setOpenSidebar(false);
            }}
            sx={{ "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" } }}
          >
            <Label sx={{ marginRight: 1, color: "white" }} />
            <ListItemText primary="Label Claims" />
          </ListItem>
          <ListItem
            button
            onClick={() => {
              setActiveSection("users");
              setOpenSidebar(false);
            }}
            sx={{ "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" } }}
          >
            <People sx={{ marginRight: 1, color: "white" }} />
            <ListItemText primary="Users" />
          </ListItem>
          <ListItem
            button
            onClick={() => {
              setActiveSection("orders");
              setOpenSidebar(false);
            }}
            sx={{ "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" } }}
          >
            <ShoppingCart sx={{ marginRight: 1, color: "white" }} />
            <ListItemText primary="Orders" />
          </ListItem>
          <ListItem
            button
            onClick={() => {
              setActiveSection("call_schedules");
              setOpenSidebar(false);
            }}
            sx={{ "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" } }}
          >
            <ShoppingCart sx={{ marginRight: 1, color: "white" }} />
            <ListItemText primary="Call Schedules" />
          </ListItem>
          <ListItem
            button
            onClick={() => navigate("/user-settings")}
            sx={{ "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" } }}
          >
            <AccountCircle sx={{ marginRight: 1, color: "white" }} />
            <ListItemText primary="Profile" />
          </ListItem>
        </List>
      </Drawer>

      <Box
        sx={{
          flexGrow: 1,
          width: openSidebar ? `calc(100% - ${drawerWidth}px)` : "100%",
          transition: "margin-left 0.3s, width 0.3s",
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          overflowX: "auto",
          backgroundColor: "#f5f5f5",
        }}
      >
        <AppBar
          position="sticky"
          sx={{
            backgroundImage: `url(${heroImage1})`,
            backgroundSize: "cover",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="open sidebar"
              onClick={() => setOpenSidebar(true)}
            >
              <Menu />
            </IconButton>
            <img src={logo} alt="Logo" style={{ height: "50px", borderRadius: "10px" }} />
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            backgroundImage: `url(${heroImage1})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            height: "15vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            color: "white",
          }}
        >
          <Typography variant="h2" sx={{ fontWeight: "700" }}>
            Welcome Admin
          </Typography>
        </Box>

        <Container sx={{ marginTop: "30px", flexGrow: 1, maxWidth: "100%" }}>
          {renderContent()}
        </Container>

        <AddLabelClaimDialog
          open={openAddDialog}
          onClose={handleAddDialogClose}
          newLabelClaim={newLabelClaim}
          setNewLabelClaim={setNewLabelClaim}
          handleAddNewLabelClaim={handleAddNewLabelClaim}
          handleFileChange={handleFileChange}
        />

        <EditLabelClaimDialog
          open={openDialog}
          onClose={handleDialogClose}
          labelClaim={labelClaim}
          setLabelClaim={setLabelClaim}
          handleUpdateLabelClaim={handleUpdateLabelClaim}
          handleFileChange={handleFileChange}
        />

        <BlockedLabelClaimsDialog
          open={blockedLabelClaimsDialogOpen}
          onClose={() => setBlockedLabelClaimsDialogOpen(false)}
          blockedLabelClaims={blockedLabelClaims}
        />
      </Box>
    </Box>
  );
}

export default AdminDashboard;